import type { GoalListItem } from '@pms/shared';
import { formatDate } from '@/lib/dateFormat';
import { cn } from '@/lib/utils';
import { DataTable, type Column } from '@/components/ui/data-table';
import { GoalProgressBar } from '@/components/goals/GoalProgressBar';
import {
  GoalPriorityBadge,
  GoalRiskBadge,
  GoalStatusBadge,
  GoalTargetTypeBadge,
} from '@/components/goals/GoalBadges';
import { formatDueLabel } from '@/components/goals/goalConstants';

interface Props {
  rows: GoalListItem[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onOpen: (id: string) => void;
  empty: string;
  pagination?: {
    page: number;
    totalPages: number;
    total?: number;
    onPageChange: (page: number) => void;
  };
}

export function GoalsTable({ rows, loading, error, onRetry, onOpen, empty, pagination }: Props) {
  const columns: Column<GoalListItem>[] = [
    {
      key: 'title',
      header: 'Goal',
      cell: (row) => (
        <div className="min-w-[200px] max-w-[320px]">
          <p className="truncate text-sm font-medium text-foreground">{row.title}</p>
          <p className="truncate text-[11px] text-muted-foreground">
            {row.category_name ?? 'Uncategorized'}
          </p>
        </div>
      ),
    },
    {
      key: 'owner',
      header: 'Employee',
      cell: (row) => (
        <div className="min-w-[130px]">
          <p className="truncate text-sm text-foreground">{row.owner_name ?? '—'}</p>
          <p className="truncate text-[11px] text-muted-foreground">{row.department_name ?? '—'}</p>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      cell: (row) => <GoalTargetTypeBadge type={row.target_type} />,
    },
    {
      key: 'progress',
      header: 'Progress',
      className: 'min-w-[160px]',
      cell: (row) => <GoalProgressBar goal={row} size="sm" />,
    },
    {
      key: 'due',
      header: 'Due',
      cell: (row) => {
        const label = formatDueLabel(row.due_date, row.status);
        return (
          <div className="min-w-[110px]">
            <p className="text-sm text-foreground">{formatDate(row.due_date)}</p>
            {label && (
              <p
                className={cn(
                  'text-[11px]',
                  row.is_overdue
                    ? 'text-rose-600 dark:text-rose-400'
                    : row.is_at_risk
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-muted-foreground',
                )}
              >
                {label}
              </p>
            )}
          </div>
        );
      },
    },
    {
      key: 'priority',
      header: 'Priority',
      cell: (row) => <GoalPriorityBadge priority={row.priority} />,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => (
        <div className="flex flex-wrap items-center gap-1">
          <GoalStatusBadge status={row.status} />
          <GoalRiskBadge isOverdue={row.is_overdue} isAtRisk={row.is_at_risk} />
        </div>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.id}
      onRowClick={(r) => onOpen(r.id)}
      loading={loading}
      error={error}
      onRetry={onRetry}
      empty={empty}
      pagination={pagination}
    />
  );
}
